import React from "react";
import styled from "styled-components";
import Modal from "react-bootstrap/Modal";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { MilestonesResponse, UnitProgress } from "../../common/api";
import {
  STAT_ICONS,
  STREAK_COLOR,
  StatIcon,
  statIconColors,
} from "./ProfileIcons";
import {
  CellCover,
  clampLines,
  DragScrollRail,
  FadedRailFrame,
  ProgressBar,
  ProgressBarFill,
} from "../../common/CommonStyles";
import {
  COLOR_SHADOW_LIGHT,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_TERTIARY,
  COLOR_WHITE,
  COLOR_YELLOW_MILESTONE,
} from "../../common/colors";
import { SPACE_SM, SPACE_MD, SPACE_XL } from "../../common/spacing";
import { useDragToScroll } from "../../common/utils";

const RAIL_TRACK_COLOR = "#EBEBE6";

const RAIL_RUNG_WIDTH = 100;
const RAIL_HEIGHT = 72;
const RAIL_TOP_INSET = 4;
const RAIL_CENTER_Y = RAIL_TOP_INSET + 28;
const RAIL_BAR_HEIGHT = 11;
const RAIL_DOT_SIZE = 24;
const RAIL_DOT_STROKE = 2;
const RAIL_MARKER_HEIGHT = 40;
const RAIL_LABEL_GAP = 4;
const RAIL_FADE_LENGTH = 75;

const PIN_HEIGHT = 34.67;

type StreakMilestone = { weeks: number; isReached: boolean };

const streakMilestones = (currentWeeks: number): StreakMilestone[] => {
  const required = [3, 5, 10, 15, 20];
  const stride = 10;
  const last = required[required.length - 1];
  const results = required.map((weeks) => ({
    weeks,
    isReached: currentWeeks >= weeks,
  }));
  if (currentWeeks >= last) {
    let weeks = last + stride;
    while (currentWeeks > weeks) {
      results.push({ weeks, isReached: true });
      weeks += stride;
    }
    results.push({ weeks, isReached: false });
  }
  return results;
};

const rungCenterX = (index: number) => (index + 0.5) * RAIL_RUNG_WIDTH;

const railProgressX = (milestones: StreakMilestone[], currentWeeks: number) => {
  if (currentWeeks <= 0) {
    return 0;
  }
  const next = milestones.findIndex((it) => it.weeks > currentWeeks);
  if (next === -1) {
    return rungCenterX(milestones.length - 1);
  }
  const low = next === 0 ? 0 : milestones[next - 1].weeks;
  const from = next === 0 ? 0 : rungCenterX(next - 1);
  const fraction = (currentWeeks - low) / (milestones[next].weeks - low);
  return from + fraction * (rungCenterX(next) - from);
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Hero = styled.div<{ $glowSize: number; $glow: string }>`
  position: relative;
  display: flex;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${(p) => p.$glowSize}px;
    height: ${(p) => p.$glowSize}px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: ${(p) => p.$glow};
  }

  svg {
    position: relative;
  }
`;

const Headline = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: ${COLOR_TEXT_PRIMARY};
`;

const Celebration = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${STREAK_COLOR};
`;

const Caption = styled.div`
  max-width: 237px;
  font-size: 12px;
  color: ${COLOR_TEXT_TERTIARY};
`;

const RailFrame = styled(FadedRailFrame)`
  align-self: stretch;
  margin-top: ${SPACE_XL}px;
`;

const RailScroller = styled(DragScrollRail)`
  padding: 0 ${SPACE_XL}px;

  svg {
    display: block;
  }
`;

const StreakPin: React.FC<{ x: number }> = ({ x }) => {
  const [width, height, , , paths] = STAT_ICONS.streak.icon.icon;
  const [secondaryPath, primaryPath] = paths as string[];
  const colors = statIconColors(STAT_ICONS.streak);
  const scale = PIN_HEIGHT / height;
  const footY = RAIL_CENTER_Y + RAIL_BAR_HEIGHT / 2;
  return (
    <g
      transform={`translate(${x - (width * scale) / 2} ${
        footY - PIN_HEIGHT
      }) scale(${scale})`}
    >
      <path d={secondaryPath} fill={colors.secondary} />
      <path d={primaryPath} fill={colors.primary} />
    </g>
  );
};

const StreakRail: React.FC<{ currentWeeks: number }> = ({ currentWeeks }) => {
  const { t } = useTranslation();
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const dragHandlers = useDragToScroll();
  const milestones = streakMilestones(currentWeeks);
  const currentIndex = milestones.map((it) => it.isReached).lastIndexOf(true);
  const progressX = railProgressX(milestones, currentWeeks);
  const width = milestones.length * RAIL_RUNG_WIDTH;
  const labelTop = RAIL_TOP_INSET + RAIL_MARKER_HEIGHT + RAIL_LABEL_GAP;

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && currentIndex >= 0) {
      scroller.scrollLeft =
        SPACE_XL + rungCenterX(currentIndex) - scroller.clientWidth / 2;
    }
  }, [currentIndex]);

  return (
    <RailFrame>
      <RailScroller ref={scrollerRef} {...dragHandlers}>
        <svg width={width} height={RAIL_HEIGHT} aria-hidden>
          <defs>
            <linearGradient
              id="streak-rail-fill"
              gradientUnits="userSpaceOnUse"
              x1={0}
              x2={progressX}
            >
              <stop offset={0} stopColor={STREAK_COLOR} stopOpacity={0} />
              <stop
                offset={Math.min(1, RAIL_FADE_LENGTH / Math.max(progressX, 1))}
                stopColor={STREAK_COLOR}
              />
            </linearGradient>
          </defs>
          <rect
            y={RAIL_CENTER_Y - RAIL_BAR_HEIGHT / 2}
            width={width}
            height={RAIL_BAR_HEIGHT}
            rx={RAIL_BAR_HEIGHT / 2}
            fill={RAIL_TRACK_COLOR}
          />
          {progressX > 0 && (
            <rect
              y={RAIL_CENTER_Y - RAIL_BAR_HEIGHT / 2}
              width={progressX}
              height={RAIL_BAR_HEIGHT}
              rx={RAIL_BAR_HEIGHT / 2}
              fill="url(#streak-rail-fill)"
            />
          )}
          {milestones.map((milestone, index) => (
            <g key={milestone.weeks}>
              {index !== currentIndex && (
                <>
                  <circle
                    cx={rungCenterX(index)}
                    cy={RAIL_CENTER_Y}
                    r={RAIL_DOT_SIZE / 2}
                    fill={milestone.isReached ? STREAK_COLOR : COLOR_WHITE}
                  />
                  <circle
                    cx={rungCenterX(index)}
                    cy={RAIL_CENTER_Y}
                    r={(RAIL_DOT_SIZE - RAIL_DOT_STROKE) / 2}
                    fill="none"
                    stroke={COLOR_SHADOW_LIGHT}
                    strokeWidth={RAIL_DOT_STROKE}
                  />
                </>
              )}
              <text
                x={rungCenterX(index)}
                y={labelTop}
                dominantBaseline="hanging"
                textAnchor="middle"
                fontSize={16}
                fontWeight={700}
                fill={
                  milestone.isReached ? COLOR_TEXT_PRIMARY : COLOR_TEXT_TERTIARY
                }
              >
                {t("milestones_streak_count", { count: milestone.weeks })}
              </text>
            </g>
          ))}
          {currentWeeks > 0 && <StreakPin x={progressX} />}
        </svg>
      </RailScroller>
    </RailFrame>
  );
};

type StatModalProps = {
  show: boolean;
  onHide: () => void;
  milestones: MilestonesResponse;
};

const StatModal: React.FC<{
  show: boolean;
  onHide: () => void;
  size?: "xl";
  scrollable?: boolean;
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ show, onHide, size, scrollable, bodyClassName, children }) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} onHide={onHide} size={size} scrollable={scrollable}>
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body className={bodyClassName}>{children}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("awards_close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const StreakModal: React.FC<StatModalProps> = ({
  show,
  onHide,
  milestones,
}) => {
  const { t } = useTranslation();
  const current = milestones.current_weekly_lesson_completion_streak_count;
  const longest = milestones.highest_weekly_lesson_completion_streak_count;

  return (
    <StatModal show={show} onHide={onHide} bodyClassName="px-0">
      <ModalBody>
        <Hero
          $glowSize={108}
          $glow={`radial-gradient(closest-side, ${STREAK_COLOR}33 0%, ${STREAK_COLOR}14 50%, ${STREAK_COLOR}00 100%)`}
        >
          <StatIcon
            icon={STAT_ICONS.streak}
            style={{ fontSize: 80, margin: "12px 0 20px" }}
            title={t("profile_streak_hero")}
          />
        </Hero>
        <Headline>{t("milestones_streak_count", { count: current })}</Headline>
        {current > 0 && current >= longest && (
          <Celebration>{t("profile_streak_longest_celebration")}</Celebration>
        )}
        <Caption>{t("profile_streak_extend_help")}</Caption>
        <StreakRail currentWeeks={current} />
      </ModalBody>
    </StatModal>
  );
};

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${SPACE_XL}px;
  padding: ${SPACE_MD}px 0;
  text-align: start;
`;

const CourseTitle = styled.div`
  margin-top: ${SPACE_SM}px;
  font-size: 10px;
  font-weight: 700;
  color: ${COLOR_TEXT_PRIMARY};
  ${clampLines(2)}
`;

const CourseProgress = styled(ProgressBar)`
  height: 4px;
  margin-top: ${SPACE_SM}px;
  border-radius: 4px;
  background-color: ${RAIL_TRACK_COLOR};
`;

const CourseProgressFill = styled(ProgressBarFill)`
  background-color: ${COLOR_TEXT_SECONDARY};
`;

const CourseCovers: React.FC<{ units: UnitProgress[] }> = ({ units }) => {
  const { t } = useTranslation();
  return (
    <CourseGrid>
      {units.map((unit) => (
        <div key={unit.id}>
          <CellCover
            url={unit.cover_image}
            alt={t("unit_cover_content_description", { title: unit.title })}
            isElevated
          />
          <CourseTitle>{unit.title}</CourseTitle>
          {unit.completed_lesson_count < unit.lesson_count && (
            <CourseProgress>
              <CourseProgressFill
                width={Math.round(
                  (unit.completed_lesson_count / unit.lesson_count) * 100
                )}
              />
            </CourseProgress>
          )}
        </div>
      ))}
    </CourseGrid>
  );
};

const CoursesTabs = styled.div`
  .nav-underline {
    --bs-nav-link-color: ${COLOR_TEXT_TERTIARY};
    --bs-nav-link-hover-color: ${COLOR_TEXT_PRIMARY};
    --bs-nav-underline-link-active-color: ${COLOR_TEXT_PRIMARY};
  }
`;

const enum CoursesTabKey {
  Completed = "completed",
  InProgress = "in-progress",
}

export const CoursesModal: React.FC<StatModalProps> = ({
  show,
  onHide,
  milestones,
}) => {
  const { t } = useTranslation();
  const { completed, inProgress } = React.useMemo(() => {
    const completed: UnitProgress[] = [];
    const inProgress: UnitProgress[] = [];
    for (const unit of milestones.units_progress) {
      if (unit.lesson_count === 0) {
        continue;
      }
      if (unit.completed_lesson_count >= unit.lesson_count) {
        completed.push(unit);
      } else if (
        unit.started_lesson_count > 0 ||
        unit.completed_lesson_count > 0
      ) {
        inProgress.push(unit);
      }
    }
    return { completed, inProgress };
  }, [milestones]);

  return (
    <StatModal show={show} onHide={onHide} size="xl" scrollable>
      <ModalBody>
        <Hero
          $glowSize={150}
          $glow={`radial-gradient(closest-side, ${COLOR_YELLOW_MILESTONE}70 0%, ${COLOR_YELLOW_MILESTONE}33 50%, ${COLOR_YELLOW_MILESTONE}00 100%)`}
        >
          <StatIcon
            icon={STAT_ICONS.courses}
            style={{ fontSize: 77, margin: "20px 0 28px" }}
            title={t("profile_courses_hero")}
          />
        </Hero>
        <Caption>{t("profile_stat_completed_caption")}</Caption>
        <Headline className="mb-4">
          {t("profile_stat_courses_value", {
            count: milestones.completed_unit_count,
          })}
        </Headline>
      </ModalBody>
      <CoursesTabs>
        <Tabs
          variant="underline"
          defaultActiveKey={CoursesTabKey.InProgress}
          justify
        >
          <Tab
            eventKey={CoursesTabKey.Completed}
            title={t("profile_stat_completed_caption")}
          >
            <CourseCovers units={completed} />
          </Tab>
          <Tab
            eventKey={CoursesTabKey.InProgress}
            title={t("profile_courses_in_progress")}
          >
            <CourseCovers units={inProgress} />
          </Tab>
        </Tabs>
      </CoursesTabs>
    </StatModal>
  );
};
